package com.siriusxm.unifiedcontent.console.player

import com.siriusxm.pia.player.AudioPlaybackListener
import com.siriusxm.pia.player.AudioTrack
import com.siriusxm.pia.player.PlaybackClock
import com.siriusxm.pia.player.PlaybackDetailsState
import com.siriusxm.pia.player.PlaybackDetailsStatus
import com.siriusxm.pia.player.PlayingDetails
import com.siriusxm.pia.player.PlayState
import com.siriusxm.pia.player.PlaybackController
import com.siriusxm.pia.player.Registration
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.w3c.dom.Audio
import org.w3c.dom.events.Event

/**
 * The headless hls.js/`<audio>`-backed playback engine — the single implementation of
 * [PlaybackController] shared by the global player bar and by app-owned headless players (UCA-1111).
 *
 * Unlike the previous design, this engine is NOT tied to a Compose composition: it owns its media
 * element, its optional hls.js instance, its observable [StateFlow]s, its credential-refresh loop, and
 * its own [CoroutineScope]. That is what makes it usable both under [AudioPlayerView] (which merely
 * observes its flows and calls its transport methods) and as a bare library object an application drives
 * itself. The global bar keeps a single long-lived controller for the app's lifetime; app-created
 * controllers must be [dispose]d by their owner.
 *
 * Playback uses native browser playback for ordinary URLs and browsers with native HLS support;
 * otherwise it drives an hls.js instance for `.m3u8` sources. [play] tears down any previous track
 * (element, engine, refresh loop) before loading the next.
 */
public class AudioPlayerController(
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) : PlaybackController {
    private val _state = MutableStateFlow(PlayState.IDLE)
    override val state: StateFlow<PlayState> = _state.asStateFlow()

    private val _track = MutableStateFlow<AudioTrack?>(null)
    override val track: StateFlow<AudioTrack?> = _track.asStateFlow()

    private val _position = MutableStateFlow(0.0)
    override val position: StateFlow<Double> = _position.asStateFlow()

    private val _duration = MutableStateFlow(0.0)
    override val duration: StateFlow<Double> = _duration.asStateFlow()

    private val _volume = MutableStateFlow(1.0)
    override val volume: StateFlow<Double> = _volume.asStateFlow()

    private val _playbackError = MutableStateFlow<String?>(null)
    override val playbackError: StateFlow<String?> = _playbackError.asStateFlow()

    private val _details = MutableStateFlow(PlaybackDetailsState())
    override val details: StateFlow<PlaybackDetailsState> = _details.asStateFlow()
    private var detailsJob: Job? = null
    private var detailsTimeline: List<PlayingDetails>? = null
    private var detailsLookupFailed = false
    private var detailsGeneration = 0L
    private var detailsRevision = 0L
    private val detailsWakeup = Channel<Unit>(Channel.CONFLATED)

    // The live media element and (for hls.js sources) its engine, plus the detach closure that removes
    // the element's listeners and pauses it. Retained so a track change tears the old element down
    // without its async "pause"/"error" listeners firing into the state that now belongs to the next
    // track (see UCA-622).
    private var audio: Audio? = null
    private var hls: Hls? = null
    private var detach: (() -> Unit)? = null

    // The credential-refresh loop for the current track, cancelled on the next track change or dispose.
    private var refreshJob: Job? = null

    // The desired output volume, applied to each new element. Kept apart from the element so a volume
    // set before any track loads still takes effect once one does.
    private var volumeLevel = 1.0

    // Installed change listeners. The controller runs one internal collector per observable (started in
    // init) that fans each emission out to every listener; a listener is delivered the current value of
    // each observable at install time so a late listener is never left blind to existing state.
    private val listeners = mutableSetOf<AudioPlaybackListener>()

    init {
        scope.launch { state.collect { value -> listeners.toList().forEach { it.onStateChanged(value) } } }
        scope.launch {
            position.collect { value -> listeners.toList().forEach { it.onPositionChanged(value, _duration.value) } }
        }
        scope.launch {
            duration.collect { value -> listeners.toList().forEach { it.onPositionChanged(_position.value, value) } }
        }
        scope.launch { volume.collect { value -> listeners.toList().forEach { it.onVolumeChanged(value) } } }
        scope.launch { playbackError.collect { value -> listeners.toList().forEach { it.onError(value) } } }
        scope.launch { details.collect { value -> listeners.toList().forEach { it.onDetailsChanged(value) } } }
        scope.launch { track.collect { value -> listeners.toList().forEach { it.onTrackChanged(value) } } }
    }

    override fun play(track: AudioTrack) {
        _track.value = track
        loadCurrentTrack()
    }

    override fun pause() {
        audio?.pause()
    }

    override fun resume() {
        audio?.play()
        detailsWakeup.trySend(Unit)
    }

    override fun stop() {
        teardown()
        _track.value = null
        _details.value = PlaybackDetailsState()
        _position.value = 0.0
        _duration.value = 0.0
        _playbackError.value = null
        _state.value = PlayState.IDLE
    }

    override fun seek(seconds: Double) {
        if (_track.value?.isLive == true) return
        audio?.let {
            it.currentTime = seconds
            _position.value = seconds
            detailsRevision++
            if (_track.value?.loadDetails != null) {
                _details.value = PlaybackDetailsState(PlaybackDetailsStatus.LOADING)
                detailsWakeup.trySend(Unit)
            }
        }
    }

    override fun setVolume(volume: Double) {
        val clamped = volume.coerceIn(0.0, 1.0)
        volumeLevel = clamped
        audio?.volume = clamped
        _volume.value = clamped
    }

    override fun setMuted(muted: Boolean) {
        audio?.muted = muted
    }

    override fun addListener(listener: AudioPlaybackListener): Registration {
        // Synchronise the new listener with existing state before it starts receiving change events.
        listener.onStateChanged(_state.value)
        listener.onPositionChanged(_position.value, _duration.value)
        listener.onVolumeChanged(_volume.value)
        listener.onError(_playbackError.value)
        listener.onTrackChanged(_track.value)
        listener.onDetailsChanged(_details.value)
        listeners.add(listener)
        return Registration { listeners.remove(listener) }
    }

    override fun dispose() {
        teardown()
        listeners.clear()
        detailsWakeup.close()
        scope.cancel()
    }

    /**
     * Tear down the current media element and hls.js engine and cancel the credential-refresh loop.
     * Leaves the observable state untouched so callers ([stop], [loadCurrentTrack]) can set the state
     * appropriate to what they are doing next.
     */
    private fun teardown() {
        detailsGeneration++
        detailsJob?.cancel()
        detailsJob = null
        detailsTimeline = null
        detailsLookupFailed = false
        detailsWakeup.tryReceive()
        refreshJob?.cancel()
        refreshJob = null
        hls?.destroy()
        hls = null
        detach?.invoke()
        detach = null
        audio = null
    }

    /**
     * Load and begin playing the current [_track], mirroring its clock/metadata/errors into the
     * observable flows. Tears down any previous element/engine first, then branches native-HLS vs
     * hls.js vs direct playback exactly as the global bar did before this engine was extracted.
     */
    private fun loadCurrentTrack() {
        teardown()
        _position.value = 0.0
        _duration.value = 0.0
        _playbackError.value = null

        val selectedTrack = _track.value ?: run {
            _state.value = PlayState.IDLE
            return
        }

        _details.value = PlaybackDetailsState(
            if (selectedTrack.loadDetails != null) PlaybackDetailsStatus.LOADING else PlaybackDetailsStatus.UNSUPPORTED
        )
        _state.value = PlayState.LOADING
        val a = Audio()
        a.volume = volumeLevel
        audio = a

        val onPause: (Event) -> Unit = {
            detailsRevision++
            _state.value = PlayState.PAUSED
        }
        val onPlaying: (Event) -> Unit = {
            // A successful (re)start clears any earlier transient error message.
            _playbackError.value = null
            detailsRevision++
            _state.value = PlayState.PLAYING
            publishTimedDetails()
            detailsWakeup.trySend(Unit)
        }
        val onWaiting: (Event) -> Unit = {
            detailsRevision++
            _state.value = PlayState.LOADING
        }
        // A native media element error (direct URLs such as podcast MP3s, and browsers with native
        // HLS). Surface whatever the browser's MediaError says; fall back to the generic message when
        // the element exposes no description.
        val onError: (Event) -> Unit = {
            val message = (a.error?.asDynamic()?.message as? String)?.takeIf { it.isNotBlank() }
            _playbackError.value = message ?: PLAYBACK_FAILED_MESSAGE
            _state.value = PlayState.PAUSED
        }
        // Mirror the element's clock/metadata so the scrubber and time readout (and app listeners) stay
        // in sync with playback, including seeks driven from elsewhere.
        val onTimeUpdate: (Event) -> Unit = {
            _position.value = a.currentTime
            publishTimedDetails()
        }
        val onDurationChange: (Event) -> Unit = {
            val d = a.duration
            _duration.value = if (d.isFinite()) d else 0.0
        }
        // Apply the track's start offset once the element can seek. hls.js is handed the offset as its
        // `startPosition` instead (below), so this applies only to native playback. One-shot: later
        // metadata reloads must not yank the playhead back to the start offset.
        val startSeconds = selectedTrack.startSeconds.takeIf { it > 0.0 }
        var startPending = startSeconds != null
        val onLoadedMetadata: (Event) -> Unit = { event ->
            onDurationChange(event)
            if (startPending && hls == null && startSeconds != null) {
                startPending = false
                a.currentTime = startSeconds
                _position.value = startSeconds
            }
        }
        a.addEventListener("pause", onPause)
        a.addEventListener("playing", onPlaying)
        a.addEventListener("waiting", onWaiting)
        a.addEventListener("error", onError)
        a.addEventListener("timeupdate", onTimeUpdate)
        a.addEventListener("durationchange", onDurationChange)
        a.addEventListener("loadedmetadata", onLoadedMetadata)
        detach = {
            a.removeEventListener("pause", onPause)
            a.removeEventListener("playing", onPlaying)
            a.removeEventListener("waiting", onWaiting)
            a.removeEventListener("error", onError)
            a.removeEventListener("timeupdate", onTimeUpdate)
            a.removeEventListener("durationchange", onDurationChange)
            a.removeEventListener("loadedmetadata", onLoadedMetadata)
            a.pause()
        }

        val isHls = selectedTrack.url.substringBefore('?').endsWith(".m3u8", ignoreCase = true)
        val hasNativeHls = a.canPlayType(HLS_MIME_TYPE).toString().isNotBlank()
        // Timed details use hls.js's program-date clock. Native HLS can play the same
        // stream but does not expose that clock through the HTML audio element.
        val needsBroadcastClock = selectedTrack.loadDetails != null
        if (isHls && (!hasNativeHls || needsBroadcastClock) && Hls.isSupported()) {
            // hls.js starts loading fragments at `startPosition`, so an offset start never fetches
            // (or plays) the head of the stream first. -1 is hls.js's "from the beginning" default.
            val config = js("{}")
            config.startPosition = startSeconds ?: -1
            Hls(config).also { engine ->
                hls = engine
                engine.attachMedia(a)
                engine.on(Hls.Events.MEDIA_ATTACHED) { _, _ ->
                    engine.loadSource(selectedTrack.url)
                }
                engine.on(Hls.Events.MANIFEST_PARSED) { _, _ ->
                    a.play()
                }
                engine.on(Hls.Events.ERROR) { _, data ->
                    // Surface fatal hls.js failures as a paused state so the control leaves LOADING and
                    // the user can retry, matching the native error path. A 403/404 on the manifest
                    // means the CloudFront/S3 origin has no object at that key — i.e. the media for this
                    // entity was never published — so we say that specifically; any other fatal error
                    // gets the generic message.
                    if (data?.fatal == true) {
                        _playbackError.value = hlsErrorMessage(data)
                        _state.value = PlayState.PAUSED
                    }
                }
            }
        } else {
            a.src = selectedTrack.url
            a.play()
        }

        startCredentialRefresh(selectedTrack)
        startDetailsPolling(selectedTrack)
    }

    /**
     * Poll presentation metadata independently of the media and credential loops. A track generation
     * and seek revision prevent late responses from replacing a newer source/position's details.
     * Pausing freezes the displayed cut; playback/seek events wake the next lookup promptly.
     */
    private fun startDetailsPolling(selectedTrack: AudioTrack) {
        val load = selectedTrack.loadDetails ?: return
        val generation = detailsGeneration
        detailsJob = scope.launch {
            var failures = 0
            while (generation == detailsGeneration) {
                state.first { it == PlayState.PLAYING }
                detailsWakeup.tryReceive()
                val revision = detailsRevision
                val clock = playbackClock()
                var waitMilliseconds = 10_000L
                try {
                    val update = withTimeoutOrNull(7000) { load(clock) }
                        ?: error("Playback details lookup timed out")
                    if (generation != detailsGeneration) return@launch
                    if (revision != detailsRevision || _state.value != PlayState.PLAYING) continue
                    failures = 0
                    if (update.pollAfterMilliseconds == null) {
                        detailsTimeline = null
                        detailsLookupFailed = false
                        _details.value = PlaybackDetailsState(PlaybackDetailsStatus.UNSUPPORTED)
                        return@launch
                    }
                    detailsLookupFailed = false
                    detailsTimeline = update.timeline
                    if (update.timeline != null) {
                        publishTimedDetails()
                    } else if (selectedTrack.isLive && update.current?.approximate == true) {
                        _details.value = PlaybackDetailsState(PlaybackDetailsStatus.WAITING_FOR_CLOCK)
                    } else {
                        _details.value = PlaybackDetailsState(
                            status = if (update.current == null) PlaybackDetailsStatus.EMPTY else PlaybackDetailsStatus.AVAILABLE,
                            current = update.current,
                        )
                    }
                    waitMilliseconds = (update.pollAfterMilliseconds ?: 10_000).coerceIn(5000, 60_000)
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (_: Exception) {
                    if (generation != detailsGeneration) return@launch
                    if (revision != detailsRevision || _state.value != PlayState.PLAYING) continue
                    failures = (failures + 1).coerceAtMost(4)
                    detailsLookupFailed = true
                    if (failures >= 3) detailsTimeline = null
                    if (detailsTimeline != null) {
                        publishTimedDetails()
                    } else {
                        _details.value = PlaybackDetailsState(PlaybackDetailsStatus.UNAVAILABLE)
                    }
                    waitMilliseconds = (10_000L shl (failures - 1)).coerceAtMost(60_000)
                }
                withTimeoutOrNull(waitMilliseconds) { detailsWakeup.receive() }
            }
        }
    }

    /**
     * Choose cached details at the audio playhead, including transitions between network polls.
     * Metadata may arrive ahead of the audio. Without a broadcast timestamp we leave it pending,
     * rather than substituting wall-clock time and displaying an upcoming cut early.
     */
    private fun publishTimedDetails() {
        val timeline = detailsTimeline ?: return
        if (_state.value != PlayState.PLAYING) return
        val timestamp = playbackClock().broadcastTime
        if (timestamp == null && timeline.isNotEmpty()) {
            _details.value = PlaybackDetailsState(PlaybackDetailsStatus.WAITING_FOR_CLOCK)
            return
        }
        val current = timeline.lastOrNull { item ->
            val start = item.startsAt
            val end = item.endsAt
            timestamp != null && start != null && start <= timestamp && (end == null || timestamp < end)
        }
        val status = when {
            detailsLookupFailed -> PlaybackDetailsStatus.UNAVAILABLE
            current == null -> PlaybackDetailsStatus.EMPTY
            else -> PlaybackDetailsStatus.AVAILABLE
        }
        _details.value = PlaybackDetailsState(status, current)
    }

    /** HLS broadcast time and relative media time are distinct clocks; never convert one into the other. */
    private fun playbackClock(): PlaybackClock {
        val date = hls?.playingDate
        val broadcastTime = date?.getTime()?.takeIf { it.isFinite() }
            ?.let { kotlin.time.Instant.fromEpochMilliseconds(it.toLong()) }
        return PlaybackClock(
            positionMilliseconds = (_position.value.coerceAtLeast(0.0) * 1000).toLong(),
            broadcastTime = broadcastTime,
        )
    }

    /**
     * Keep short-lived playback credentials fresh while a credential-backed track plays. The stream URL
     * never changes — refreshing re-issues the CloudFront signed cookies so playback of episodes longer
     * than the cookie TTL (and resuming after a long pause) keeps working. Scoped to the current track:
     * a track change or [dispose] cancels it. No-op for tracks without refresh information.
     */
    private fun startCredentialRefresh(selectedTrack: AudioTrack) {
        val refresh = selectedTrack.refreshCredentials ?: return
        val initialExpiry = selectedTrack.credentialsExpireAt ?: return
        refreshJob = scope.launch {
            var expiresAt = initialExpiry
            while (true) {
                // Renew CREDENTIAL_REFRESH_LEAD before expiry, but never busy-loop if we are already
                // past the lead window (clamped to zero, i.e. refresh immediately).
                val wait = expiresAt - Clock.System.now() - CREDENTIAL_REFRESH_LEAD
                if (wait.isPositive()) {
                    delay(wait)
                }
                expiresAt = try {
                    refresh() ?: return@launch
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (error: Throwable) {
                    // A failed refresh is not fatal to the current buffer; stop scheduling and let the
                    // native/hls error path report if playback eventually stalls.
                    console.error("Failed to refresh playback credentials", error)
                    return@launch
                }
            }
        }
    }
}

/** Generic message shown when a track cannot be played and no more specific reason is known. */
internal const val PLAYBACK_FAILED_MESSAGE = "This media could not be played. It may not be available yet."

/**
 * Message shown when the origin returned 403/404. Because playback access (the CloudFront key group and
 * cross-account bucket grants) is verified per environment, a 403/404 at play time means the audio for
 * this entity simply is not in the bucket — so we state that as the cause rather than hedging about
 * permissions.
 */
internal const val MEDIA_NOT_AVAILABLE_MESSAGE = "The media is unavailable"

/**
 * Map a fatal hls.js ERROR payload to a user-facing message. hls.js reports the failed HTTP request's
 * status on `data.response.code` (present for network errors such as a manifest, playlist, or segment
 * load). A 403 or 404 there means CloudFront/S3 has no object at the signed key — the entity's media was
 * never published — so we return the "not available" message. Everything else (media decode errors, mux
 * errors, network errors without a 403/404 status) falls back to the generic message.
 *
 * The payload is `dynamic` because it comes straight from the hls.js callback; the status is read
 * defensively (it can be absent or non-numeric) so a malformed payload never throws in the handler.
 */
internal fun hlsErrorMessage(data: dynamic): String {
    val code: Int? = (data?.response?.code as? Number)?.toInt()
    return if (code == 403 || code == 404) MEDIA_NOT_AVAILABLE_MESSAGE else PLAYBACK_FAILED_MESSAGE
}

/** The MIME type CloudFront/Apple HLS playlists advertise. */
internal const val HLS_MIME_TYPE = "application/vnd.apple.mpegurl"

/**
 * How far ahead of credential expiry the engine renews CloudFront cookies. Large enough to absorb the
 * PreparePlayback round-trip and clock skew, small enough to stay well inside a 300s cookie TTL.
 */
private val CREDENTIAL_REFRESH_LEAD = 30.seconds

/** Minimal hls.js surface needed by the audio engine. */
@JsModule("hls.js")
@JsNonModule
internal external class Hls(config: dynamic = definedExternally) {
    companion object {
        fun isSupported(): Boolean
        val Events: HlsEvents
    }

    val playingDate: kotlin.js.Date?

    fun attachMedia(media: Audio)
    fun loadSource(url: String)
    fun on(event: String, callback: (event: String, data: dynamic) -> Unit)
    fun destroy()
}

/** Event names exposed as static properties by hls.js. */
internal external interface HlsEvents {
    val MEDIA_ATTACHED: String
    val MANIFEST_PARSED: String
    val ERROR: String
}
